import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2, FileText, ArrowRight, Clock, ShieldCheck, IndianRupee } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { servicesData } from '../servicesData';
import { useToast } from '../hooks/use-toast';
import { useState } from 'react';
import { submitEnquiry } from '../api';

export default function ServicePage() {
  const { slug } = useParams();
  const service = servicesData[slug];
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', mobile: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  if (!service) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast({ title: 'Missing details', description: 'Please enter your name and mobile number.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, service: service.title, source: `service_page:${slug}` });
      toast({ title: 'Request received!', description: `Thanks ${form.name}, our ${service.title} expert will call you shortly.` });
      setForm({ name: '', mobile: '', email: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#f4f9fd] via-white to-[#fff4ec] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <nav className="text-xs text-slate-500 mb-4">
              <Link to="/" className="hover:text-orange-600">Home</Link>
              <span className="mx-1.5">/</span>
              <span className="text-slate-700">{service.title}</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-white border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <ShieldCheck className="w-4 h-4" /> {service.tagline}
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#0b3d5b] leading-tight">{service.title}</h1>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">{service.hero}</p>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-baseline gap-1">
                <IndianRupee className="w-5 h-5 text-[#0b3d5b]" />
                <span className="text-3xl font-extrabold text-[#0b3d5b]">{service.price.replace('\u20b9', '')}</span>
              </div>
              <span className="text-xs text-slate-500">{service.priceNote}</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#lead-form" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-lg transition-colors">Talk to Expert</a>
              <a href="#process" className="bg-white border border-slate-200 hover:border-orange-300 text-[#0b3d5b] font-semibold px-5 py-3 rounded-lg transition-colors">How it works</a>
            </div>
          </div>
          <div id="lead-form" className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">Free Consultation</div>
            <h3 className="text-2xl font-bold text-[#0b3d5b] mt-1">Get {service.title} Quote</h3>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input type="text" placeholder="Full Name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <input type="tel" placeholder="Mobile Number*" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white font-semibold py-3 rounded-lg transition-colors">{submitting ? 'Sending…' : 'Request Callback'}</button>
              <p className="text-xs text-slate-500 text-center">100% confidential · No spam calls</p>
            </form>
          </div>
        </div>
      </section>

      {/* Features + Documents */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-8">
          <div className="bg-slate-50/60 rounded-2xl p-6 md:p-8 border border-slate-100">
            <h2 className="text-2xl font-bold text-[#0b3d5b] flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-orange-500" /> What&apos;s Included</h2>
            <ul className="mt-5 space-y-3">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm md:text-[15px]">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50/60 rounded-2xl p-6 md:p-8 border border-slate-100">
            <h2 className="text-2xl font-bold text-[#0b3d5b] flex items-center gap-2"><FileText className="w-6 h-6 text-orange-500" /> Documents Required</h2>
            <ul className="mt-5 space-y-3">
              {service.documents.map((d) => (
                <li key={d} className="flex items-start gap-2 text-slate-700">
                  <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0 mt-1" />
                  <span className="text-sm md:text-[15px]">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="bg-gradient-to-b from-white to-[#f7fbfd] py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-orange-600">Simple &amp; Transparent</div>
            <h2 className="text-2xl md:text-4xl font-extrabold text-[#0b3d5b] mt-2">How it Works</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.process.map((p, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-lg transition-all">
                <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center shadow">{i + 1}</div>
                <div className="font-bold text-[#0b3d5b] text-lg mt-2">{p.step}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center items-center gap-2 text-sm text-slate-600">
            <Clock className="w-4 h-4 text-orange-500" />
            Average completion: 3–7 working days
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b3d5b]">{service.title} FAQs</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {service.faqs.map((f, i) => (
              <AccordionItem key={i} value={`sq-${i}`} className="border border-slate-200 rounded-xl px-5 bg-slate-50/40 data-[state=open]:bg-white data-[state=open]:shadow-md transition-all">
                <AccordionTrigger className="text-left font-semibold text-[#0b3d5b] hover:text-orange-600 py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-4 text-[15px]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related services */}
      <section className="bg-slate-50/60 py-14 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-xl font-bold text-[#0b3d5b] mb-6">Related Services</h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(servicesData).filter(([s]) => s !== slug).slice(0, 4).map(([s, data]) => (
              <Link key={s} to={`/services/${s}`} className="group bg-white rounded-xl border border-slate-100 p-5 hover:shadow-lg hover:border-orange-200 transition-all">
                <div className="font-semibold text-[#0b3d5b] group-hover:text-orange-600">{data.title}</div>
                <div className="mt-1 text-xs text-slate-500">Starting {data.price}</div>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-orange-600">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
